import {
  EventSourceMessage,
  fetchEventSource,
} from '@microsoft/fetch-event-source'

type StreamOptions<T> = {
  url: string
  method?: 'POST' | 'GET'
  body?: Record<string, unknown>
  headers?: Record<string, string>
  signal?: AbortSignal
  onMessage: (event: EventSourceMessage) => T | null
  onError?: (error: Error) => T | null
}

/**
 * Creates an async generator that streams events from a Server-Sent Events (SSE) endpoint.
 * Handles event buffering, error handling, and proper cleanup.
 *
 * @param options - Configuration for the streaming request
 * @param options.onMessage - Callback to parse and transform SSE message events into your event type
 * @param options.onError - Optional callback to transform errors into your event type
 * @returns AsyncGenerator that yields events of type T
 *
 * @example
 * ```ts
 * for await (const event of createStreamingAsyncGenerator<MyEventType>({
 *   url: 'https://api.example.com/stream',
 *   body: { message: 'Hello' },
 *   headers: { 'Authorization': 'Bearer token' },
 *   signal: abortController.signal,
 *   onMessage: (event) => {
 *     const data = JSON.parse(event.data)
 *     if (event.event === 'delta') return { type: 'delta', text: data.text }
 *     return null
 *   },
 *   onError: (error) => ({ type: 'error', message: error.message })
 * })) {
 *   if (event.type === 'delta') {
 *     console.log(event.text)
 *   }
 * }
 * ```
 */
export async function* createStreamingAsyncGenerator<T>(
  options: StreamOptions<T>
): AsyncGenerator<T, void, unknown> {
  const {
    url,
    method = 'POST',
    body,
    headers = {},
    signal,
    onMessage,
    onError,
  } = options

  const eventBuffer: T[] = []
  let resolveWaiting: (() => void) | null = null
  let streamEnded = false

  const waitForEvent = () =>
    new Promise<void>((resolve) => {
      resolveWaiting = resolve
    })

  const addEvent = (event: T) => {
    eventBuffer.push(event)
    if (resolveWaiting) {
      resolveWaiting()
      resolveWaiting = null
    }
  }

  const endStream = (error?: Error) => {
    if (error && onError) {
      const errorEvent = onError(error)
      if (errorEvent) {
        addEvent(errorEvent)
      }
    }
    streamEnded = true
    if (resolveWaiting) {
      resolveWaiting()
      resolveWaiting = null
    }
  }

  fetchEventSource(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'x-mydra-app': 'aiBusiness',
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
    signal,
    openWhenHidden: true,

    onopen: async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
    },
    onmessage: (event) => {
      try {
        const parsedEvent = onMessage(event)
        if (parsedEvent !== null) {
          addEvent(parsedEvent)
        }
      } catch {
        // Ignore parse errors
      }
    },
    onerror: (error) => {
      endStream(
        error instanceof Error ? error : new Error('Stream error occurred')
      )
    },
    onclose: () => {
      endStream()
    },
  }).catch(endStream)

  try {
    while (!streamEnded || eventBuffer.length > 0) {
      if (eventBuffer.length > 0) {
        yield eventBuffer.shift()!
      } else if (!streamEnded) {
        await waitForEvent()
      }
    }
  } catch (error) {
    if (onError) {
      const errorEvent = onError(
        error instanceof Error ? error : new Error('Stream error occurred')
      )
      if (errorEvent) {
        yield errorEvent
      }
    }
  }
}
